import type {
  MigrationManifest,
  PersistConfig,
  PersistedState
} from "redux-persist";
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from "redux-persist";

import { combineReducers, configureStore } from "@reduxjs/toolkit";

import { AsyncStorage } from "@/store/tabStore/types";

import { clearOtherTabStorages } from "./tabStore";

import { MIGRATION_KEYS } from "./migrations/constant";
import { analysisJobReducer, resetAnalysisJob } from "./slice/analysisJob";
import {
  analysisSummaryReducer,
  resetAnalysisSummaryState
} from "./slice/analysisSummary";
import { authReducer, resetAuth } from "./slice/authSlice";
import type { DatasetState } from "./slice/datasetSlice";
import { datasetReducer, resetDatasetState } from "./slice/datasetSlice";
import {
  globalFilterReducer,
  GlobalFilterState,
  resetGlobalFilter
} from "./slice/globalFilterSlice";
import { mapReducer, resetMapState } from "./slice/mapSlice";
import { exitOnboardingMode, onboardingReducer } from "./slice/onboardingSlice";
import { resetSpatialState, spatialReducer } from "./slice/spatialSlice";
import { resetUIDataConfig, uiReducer } from "./slice/uiSlice";
import { resetWidgetState, widgetReducer } from "./slice/widgetSlice";
import { sharedStorage, tabStorage } from "./tabStore/storage";

type MigrationManifestTyped<S> = {
  [version: number]: (
    state: (S & PersistedState) | undefined
  ) => S | undefined | Promise<S | undefined>;
};

/** redux-persist's KEY_PREFIX — declared in its types but not exported at runtime. */
const PERSIST_KEY_PREFIX = "persist:";

/**
 * Reads persisted state from both the legacy serialized format and the new
 * `serialize: false` format, so existing state is preserved after migration.
 *
 * The stored object's type determines the format:
 * - Legacy: the stored object and its values are JSON strings.
 * - New: values are already parsed and must not be parsed again, since valid
 *   string values such as `analysisId` may not be valid JSON.
 */
const getStoredStateCompat = async <S>(
  config: PersistConfig<S>
): Promise<PersistedState> => {
  const raw = await config.storage.getItem(
    `${PERSIST_KEY_PREFIX}${config.key}`
  );

  if (!raw) return undefined;

  const isLegacyFormat = typeof raw === "string";
  const envelope = (isLegacyFormat ? JSON.parse(raw) : raw) as Record<
    string,
    unknown
  >;

  return Object.fromEntries(
    Object.entries(envelope).map(([sliceKey, value]) => [
      sliceKey,
      isLegacyFormat ? JSON.parse(value as string) : value
    ])
  ) as PersistedState;
};

type CreatePersistConfigParams<S> = {
  key: string;
  version: number;
  storage: AsyncStorage;
  migrations?: MigrationManifestTyped<S>;
  whitelist?: string[];
  blacklist?: string[];
  /**
   * redux-persist rehydrate timeout. 0 (disabled) for per-tab slices: their reads
   * park behind the readiness gate, and the default 5s could fire mid-migration
   * and rehydrate empty state — losing the tab's data.
   */
  timeout?: number;
};

export const createPersistConfig = <S>({
  key,
  version,
  storage,
  migrations = {},
  whitelist,
  blacklist,
  timeout
}: CreatePersistConfigParams<S>) => {
  const baseMigrate = createMigrate(migrations as MigrationManifest, {
    debug: import.meta.env.MODE === "development"
  });

  const migrate = async (state: PersistedState, currentVersion: number) => {
    const fromVersion = state?._persist?.version ?? 0;

    if (fromVersion >= currentVersion) {
      return state;
    }

    return baseMigrate(state, currentVersion);
  };

  return {
    key,
    version,
    storage,
    whitelist,
    blacklist,
    timeout,
    /**
     * IndexedDB already stores structured data, so JSON serialization adds
     * unnecessary overhead and memory usage. Keeping structured cloning also
     * preserves values like `undefined`, `NaN`, and `Date`.
     */
    serialize: false,

    /**
     * Reads state written in either the legacy or new storage format, so changing
     * `serialize` does not reset existing state. See getStoredStateCompat.
     */
    getStoredState: getStoredStateCompat,
    migrate: Object.keys(migrations).length ? migrate : undefined
  };
};

const mapPersistConfig = createPersistConfig({
  key: "map",
  version: 0,
  storage: tabStorage,
  timeout: 0,
  blacklist: ["selectedFeature"]
});

const spatialPersistConfig = createPersistConfig({
  key: "spatial",
  version: 0,
  storage: tabStorage,
  timeout: 0,
  blacklist: [
    "analysisResultOpenSource",
    "catchmentSettingsTemp",
    "adminAreaSettingsDataTemp",
    "adminTreeSettingsTemp"
  ]
});

const datasetPersistConfig = createPersistConfig<DatasetState>({
  key: "dataset",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

const analysisSummaryPersistConfig = createPersistConfig({
  key: "analysisSummary",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

const uiPersistConfig = createPersistConfig({
  key: "ui",
  version: 0,
  storage: tabStorage,
  timeout: 0,
  blacklist: [
    "showPolygonPopup",
    "allowPinMarker",
    "showAreaAdminPopup",
    "showCatchmentPopup",
    "showDatasetPopup",
    "showDataWeightSettingsPopup",
    "showSpatialAnalysisResultTabularModal",
    "sideBarMenuTypePopupDisplay",
    "isExpandLegendVisualization",
    "showProjectList",
    "showLegendInfo",
    "sidebarUserSettingPopupDisplay",
    "showSearchLocationPopup",
    "markerInfo",
    "showGlobalFilterPopup",
    "showLoadingOverlay"
  ]
});

const authPersistConfig = createPersistConfig({
  key: "auth",
  version: 0,
  storage: sharedStorage
});

const analysisJobPersistConfig = createPersistConfig({
  key: "analysisJob",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

const globalFilterPersistConfig = createPersistConfig({
  key: "globalFilter",
  version: 1,
  storage: tabStorage,
  timeout: 0,
  blacklist: ["adminAreaSettingsDataTemp", "selectedAreaIdsTemp"],
  migrations: {
    /**
     * Version 1: Remove LinkedDatasetID from globalFilter state as it is no longer needed after the dataset state has been migrated to remove linkedDatasetID.
     * This migration will ensure that any existing persisted state that still contains linkedDatasetID will have it removed.
     * The linkedDatasetID will be temporarily stored in localforage during the migration process and will be used in the App component to update the dataset state accordingly before being removed from localforage.
     */
    1: async (state) => {
      const legacyState = state as
        | (GlobalFilterState & { linkedDatasetIds?: string[] })
        | undefined;

      if (!legacyState) return state;

      try {
        if (legacyState.linkedDatasetIds?.length) {
          await tabStorage.setItem(
            MIGRATION_KEYS.LINKED_DATASET_IDS,
            legacyState.linkedDatasetIds
          );
        }
      } catch (e) {
        console.error(
          "[globalFilter migration v1] Failed to save linkedDatasetIds:",
          e
        );
      }

      delete legacyState.linkedDatasetIds;
      return legacyState;
    }
  }
});

const widgetPersistConfig = createPersistConfig({
  key: "widget",
  version: 0,
  storage: tabStorage,
  timeout: 0
});

// onboarding must persist active, moduleCode, and snapshot together.
// Without snapshot, exiting onboarding mode after a refresh has nothing to restore to.
const onboardingPersistConfig = createPersistConfig({
  key: "onboarding",
  version: 0,
  storage: sharedStorage,
  blacklist: ["activeStepTourId"]
});

export const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  map: persistReducer(mapPersistConfig, mapReducer),
  dataset: persistReducer(datasetPersistConfig, datasetReducer),
  spatial: persistReducer(spatialPersistConfig, spatialReducer),
  ui: persistReducer(uiPersistConfig, uiReducer),
  analysisSummary: persistReducer(
    analysisSummaryPersistConfig,
    analysisSummaryReducer
  ),
  analysisJob: persistReducer(analysisJobPersistConfig, analysisJobReducer),
  globalFilter: persistReducer(globalFilterPersistConfig, globalFilterReducer),
  widget: persistReducer(widgetPersistConfig, widgetReducer),
  onboarding: persistReducer(onboardingPersistConfig, onboardingReducer)
});

export const store = configureStore({
  devTools: import.meta.env.DEV,
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Actions from redux-persist are ignored because they may contain non-serializable values
        // and are essential for the persistence mechanism to work correctly.
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
});

export const persistor = persistStore(store);

// Function to reset the store
// We can custom the store we want to reset
export const resetStore = async () => {
  store.dispatch(resetAnalysisSummaryState());
  store.dispatch(resetAuth());
  store.dispatch(resetDatasetState());
  store.dispatch(resetMapState());
  store.dispatch(resetSpatialState());
  store.dispatch(resetUIDataConfig());
  store.dispatch(resetAnalysisJob());
  store.dispatch(resetGlobalFilter());
  store.dispatch(resetWidgetState());
  store.dispatch(exitOnboardingMode());
};

/** Logout cleanup: reset in-memory slices, then wipe other tabs' per-tab storage (security boundary). */
export const logoutCleanupStore = async () => {
  resetStore();
  await clearOtherTabStorages();
};

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
